/**
 * Central user-facing surface for NON-401 network failures (A18 slice 4e —
 * the toast leg deferred from the auth-expiry slice).
 *
 * Apollo's `onError` link runs in a module-level singleton client, so — like
 * `auth-expiry.ts` — it cannot use React context directly. The app installs a
 * toast-pushing handler via `setNetworkErrorToastHandler` (see
 * `AppToastProvider`), and the error link calls `notifyNetworkError()` when a
 * request dies on the network with a non-401 status.
 *
 * Scope: NETWORK errors only. GraphQL resolver errors already surface at the
 * page level (ErrorState / InlineError under `errorPolicy: 'all'`) — toasting
 * them too would double-report the same failure.
 */

type NetworkErrorToastHandler = () => void;

let handler: NetworkErrorToastHandler | null = null;
let lastNotifiedAtMs: number | null = null;

/**
 * A burst of parallel queries can all fail in the same tick (server down,
 * connection drop). One toast covers the lot; further notifications inside
 * the window are ignored.
 */
export const NETWORK_TOAST_THROTTLE_MS = 8000;

/**
 * Install (or clear) the toast handler. Installing resets the throttle window
 * (also how tests reset between cases).
 */
export function setNetworkErrorToastHandler(fn: NetworkErrorToastHandler | null): void {
  handler = fn;
  lastNotifiedAtMs = null;
}

/**
 * Notify the installed handler of a network failure. No-op until a handler is
 * installed (boot / tests); throttled to one toast per window.
 */
export function notifyNetworkError(nowMs: number = Date.now()): void {
  if (handler === null) return;
  if (lastNotifiedAtMs !== null && nowMs - lastNotifiedAtMs < NETWORK_TOAST_THROTTLE_MS) return;
  lastNotifiedAtMs = nowMs;
  handler();
}
